import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router';
import { Banner, useApiClient, useQuery } from '@acc/web-shared';
import { addDays, isInProgress, missedKindLabel, QUOTE_TYPES, routing, todayIn, type Approval, type LifeStatus, type PcStatus, type QuoteType, type Routing } from '@acc/domain';
import { PageHead, shortDate, useAgency } from '../agency';

export interface Opportunity {
  opportunityId: string;
  intakeId: string | null;
  customerName: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  currentInsurance: string | null;
  type: QuoteType;
  origin: string;
  pcStatus: PcStatus | null;
  lifeStatus: LifeStatus | null;
  approval: Approval;
  ownerMembershipId: string | null;
  dueDate: string | null;
  renewalDate: string | null;
  policyNumber: string | null;
  finalized: 'Thank You' | 'Life Thank You' | 'Gift Box' | null;
  acord: 'ACORD Sent' | 'Client Cancel' | null;
  referredBy: string | null;
  needs: string | null;
  notes: string | null;
  createdAt: string;
  assignedAt: string | null;
  updatedAt: string;
  version: number;
  routing?: Routing;
  reopenPending?: boolean;
}

const ROUTE_CLASS: Record<Routing, string> = { 'In Progress': 'ip', 'Completed Sales': 'done', 'Requotes / Follow-up': 'fu', 'Completed + Follow-up': 'mix' };
export const RoutingPill = ({ o }: { o: Pick<Opportunity, 'pcStatus' | 'lifeStatus'> }) => { const r = routing(o); return <span className={`pill ${ROUTE_CLASS[r]}`}>{r}</span>; };
export const StatusText = ({ s }: { s: string | null }) => (s ? <span className={s === 'Missed' ? 'pill missed' : s === 'Completed' ? 'pill done' : ''}>{s}</span> : <span className="muted">—</span>);

const QUOTE_TABS = ['All', 'Quoting', 'More Info', 'Appointment Scheduled', 'Schedule Closing'] as const;
type QuoteTab = (typeof QUOTE_TABS)[number];
type QuoteSort = 'newest' | 'oldest' | 'due' | 'customer';
const TYPE_COLORS: Record<string, [string, string]> = {
  'Cold Call': ['#171717', '#ffffff'], 'Win Back': ['#fef08a', '#713f12'],
  'Current FB': ['#bbf7d0', '#166534'], 'Break Off': ['#e9d5ff', '#6b21a8'],
  New: ['#bfdbfe', '#1d4ed8'], Requote: ['#e5e7eb', '#374151'],
  Progressive: ['#7f1d1d', '#ffffff'],
};
const PC_COLORS: Record<string, [string, string]> = {
  Quoting: ['#fecaca', '#991b1b'], 'More Info': ['#fef08a', '#854d0e'],
  'Schedule Closing': ['#fed7aa', '#9a3412'], 'Appointment Scheduled': ['#bbf7d0', '#166534'],
  Completed: ['#e5e7eb', '#374151'], Missed: ['#bfdbfe', '#1d4ed8'],
  Brokerage: ['#7f1d1d', '#ffffff'],
};
const LIFE_COLORS: Record<string, [string, string]> = {
  Interview: ['#e9d5ff', '#6b21a8'], 'Outstanding (WF)': ['#fecaca', '#991b1b'],
  'Final Requirements': ['#fef08a', '#854d0e'], 'Schedule Closing': ['#fed7aa', '#9a3412'],
  'Appointment Scheduled': ['#bbf7d0', '#166534'], Completed: ['#e5e7eb', '#374151'],
  Missed: ['#bfdbfe', '#1d4ed8'],
};
const QuoteBadge = ({ value, kind }: { value: string | null; kind: 'type' | 'pc' | 'life' }) => {
  if (!value) return <span className="muted">—</span>;
  const pair = (kind === 'type' ? TYPE_COLORS : kind === 'pc' ? PC_COLORS : LIFE_COLORS)[value];
  return <span className="pill" style={pair ? { background: pair[0], color: pair[1] } : undefined}>{value}</span>;
};

export function QuoteTable({ rows, empty }: { rows: Opportunity[]; empty: string }) {
  const a = useAgency();
  const nav = useNavigate();
  const today = todayIn();
  if (!rows.length) return <div className="empty">{empty}</div>;
  return (
    <table className="data">
      <thead><tr><th>Customer</th><th>Type</th><th>P/C Status</th><th>Life Status</th><th>Assigned To</th><th>Due Date</th><th>Action</th></tr></thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.opportunityId} className="link" onClick={() => nav(`${a.base}/quotes/${o.opportunityId}`)}>
            <td><Link to={`${a.base}/quotes/${o.opportunityId}`} onClick={(e) => e.stopPropagation()}>{o.customerName}</Link></td>
            <td><QuoteBadge value={o.type} kind="type" /></td>
            <td><QuoteBadge value={o.pcStatus} kind="pc" />{o.approval === 'Pending' && <div className="muted" style={{ fontSize: 12 }}>awaiting approval</div>}</td>
            <td><QuoteBadge value={o.lifeStatus} kind="life" /></td>
            <td>{a.name(o.ownerMembershipId)}</td>
            <td className={o.dueDate && o.dueDate < today && routing(o) === 'In Progress' ? 'due-past' : ''}>{shortDate(o.dueDate)}</td>
            <td><Link className="btn small" to={`${a.base}/quotes/${o.opportunityId}`} onClick={(e) => e.stopPropagation()}>Open</Link>{o.reopenPending && <div className="muted" style={{ fontSize: 12 }}>reopen requested</div>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function QuoteList() {
  const a = useAgency();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: QuoteTab = QUOTE_TABS.find((t) => t === tabParam) ?? 'All';
  const mine = params.get('view') === 'mine';
  const due = params.get('due');
  const [search, setSearch] = useState('');
  const [owner, setOwner] = useState(mine ? a.me.membershipId : '');
  const [type, setType] = useState('');
  const [pcFilter, setPcFilter] = useState('');
  const [lifeFilter, setLifeFilter] = useState('');
  const [sort, setSort] = useState<QuoteSort>('newest');
  // Request the authorized all-view, then narrow to active quote management rows.
  // Other workflow screens retain their existing route/view behavior.
  const q = useQuery<{ opportunities: Opportunity[] }>(a.path('/opportunities?view=all'));
  const today = todayIn();
  const rows = useMemo(() => (q.data?.opportunities ?? []).filter((o) =>
    isInProgress(o) &&
    (tab === 'All' || o.pcStatus === tab || o.lifeStatus === tab) &&
    (!search || o.customerName.toLowerCase().includes(search.toLowerCase()) || (o.policyNumber ?? '').toLowerCase().includes(search.toLowerCase())) &&
    (!type || o.type === type) &&
    (!pcFilter || o.pcStatus === pcFilter) &&
    (!lifeFilter || o.lifeStatus === lifeFilter) &&
    (!owner || o.ownerMembershipId === owner) &&
    (due !== 'overdue' || (o.dueDate !== null && o.dueDate < today)) &&
    (due !== 'week' || (o.dueDate !== null && o.dueDate >= today && o.dueDate <= addDays(today, 7))))
    .sort((x, y) => sort === 'customer'
      ? x.customerName.localeCompare(y.customerName)
      : sort === 'due'
        ? (x.dueDate ?? '9999-12-31').localeCompare(y.dueDate ?? '9999-12-31')
        : sort === 'oldest'
          ? x.createdAt.localeCompare(y.createdAt)
          : y.createdAt.localeCompare(x.createdAt)),
    [q.data, tab, search, type, pcFilter, lifeFilter, owner, due, today, sort]);
  const clearFilters = () => {
    setSearch(''); setType(''); setPcFilter(''); setLifeFilter(''); setOwner(''); setSort('newest');
    setParams({ tab: 'All' });
  };
  return (
    <>
      <PageHead title="Quotes" sub="Each opportunity tracks P/C and Life independently.">
        {a.manager && <Link className="btn primary" to={`${a.base}/quotes/new`}>New quote</Link>}
      </PageHead>
      <div className="tabs" role="tablist" aria-label="Quote status">
        {QUOTE_TABS.map((t) => <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => setParams({ tab: t })}>{t}</button>)}
      </div>
      <div className="toolbar">
        <input type="search" placeholder="Search customer or policy number" aria-label="Search quotes" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="inline" aria-label="Quote type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All quote types</option>
          {QUOTE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="inline" aria-label="Filter quotes by property and casualty coverage" value={pcFilter} onChange={(e) => setPcFilter(e.target.value)}>
          <option value="">All P/C statuses</option>
          {Object.keys(PC_COLORS).map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
        <select className="inline" aria-label="Filter quotes by life coverage" value={lifeFilter} onChange={(e) => setLifeFilter(e.target.value)}>
          <option value="">All Life statuses</option>
          {Object.keys(LIFE_COLORS).map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
        <select className="inline" aria-label="Assigned to" value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="">All employees</option>
          {a.team.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email}</option>)}
        </select>
        <select className="inline" aria-label="Sort quotes" value={sort} onChange={(e) => setSort(e.target.value as QuoteSort)}>
          <option value="newest">Newest first</option>
          <option value="due">Due date</option>
          <option value="oldest">Oldest first</option>
          <option value="customer">Customer name</option>
        </select>
        <button type="button" className="btn" onClick={clearFilters}>Clear Filters</button>
        {due && <span className="pill fu">{due === 'overdue' ? 'Overdue only' : 'Due in 7 days'} <Link to={`${a.base}/quotes`} style={{ marginLeft: 6 }}>clear</Link></span>}
      </div>
      {q.error && <Banner tone="bad">{q.error.message}</Banner>}
      <div className="card">{q.loading ? <p className="muted">Loading…</p> : <QuoteTable rows={rows} empty="No quotes match." />}</div>
    </>
  );
}

export function QuoteNew() {
  const a = useAgency();
  const api = useApiClient();
  const nav = useNavigate();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const [f, setF] = useState({ customerName: '', type: 'New' as QuoteType, phone: '', email: '', ownerMembershipId: '', dueDate: '', referredBy: '', needs: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const o = await api<Opportunity>(a.path('/opportunities'), { method: 'POST', body: { ...f, ownerMembershipId: f.ownerMembershipId || null, dueDate: f.dueDate || null } });
      refreshCounts();
      nav(`${a.base}/quotes/${o.opportunityId}`, { replace: true });
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }
  if (!a.manager) return <Banner tone="warn">New quotes start as a quote request. <Link to={`${a.base}/intake/new`}>Submit a request</Link></Banner>;
  return (
    <>
      <div className="crumbs"><Link to={`${a.base}/quotes`}>Quotes</Link> / New</div>
      <PageHead title="New quote" />
      {error && <Banner tone="bad">{error}</Banner>}
      <form className="card" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">Customer name<input required maxLength={200} value={f.customerName} onChange={set('customerName')} /></label>
          <label className="field">Quote type<select value={f.type} onChange={set('type')}>{QUOTE_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
          <label className="field">Phone<input type="tel" value={f.phone} onChange={set('phone')} /></label>
          <label className="field">Email<input type="email" value={f.email} onChange={set('email')} /></label>
          <label className="field">Owner<select value={f.ownerMembershipId} onChange={set('ownerMembershipId')}><option value="">Unassigned</option>{a.team.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email}</option>)}</select></label>
          <label className="field">Due date<input type="date" value={f.dueDate} onChange={set('dueDate')} /></label>
          <label className="field full">Referred by<input maxLength={200} value={f.referredBy} onChange={set('referredBy')} /></label>
          <label className="field full">Needs<textarea maxLength={4000} value={f.needs} onChange={set('needs')} /></label>
        </div>
        <div className="actions"><Link className="btn" to={`${a.base}/quotes`}>Cancel</Link><button className="btn primary" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create quote'}</button></div>
      </form>
    </>
  );
}

export const missedLabel = missedKindLabel;
